import styled, {css} from 'styled-components'
import {Splide, SplideSlide} from '@splidejs/react-splide'
import {Button} from '@/components/ui/button/Button'
import {InfoIcon, OpenGiftIcon} from '@/assets/icons/icons'

export const StyledContentContainer = styled.div<{hasCoupon?: boolean}>`
    ${({hasCoupon = false, theme: {spacing}}) => css`
        border-radius: 14px;
        box-shadow: rgba(0, 0, 0, 0.25) 0px 4px 10px 0px;
        padding: ${spacing * 2}px;
        ${hasCoupon &&
        css`
            border-radius: 8px;
            border: 2px solid #fc9f21;
            background: #fbfbe1;
            box-shadow: 0px 2px 8px 0px rgba(2, 0, 49, 0.25);
        `}
    `}
`

export const StyledSplide = styled(Splide)`
    ${({theme: {palette}}) => css`
        position: relative;
        width: 100%;
        display: grid;
        grid-template-columns: 1fr;
        height: 100%;
        .splide__pagination__page {
            height: 10px;
            width: 10px;
            border-radius: 50%;
            background-color: ${palette.neutral['75']};
        }
        .splide__pagination__page.is-active {
            height: 12px;
            width: 12px;
            opacity: 1;
            border-radius: 50%;
            background-color: ${palette.primary['900']};
            transform: none;
        }
    `}
`

export const StyledSplideSlide = styled(SplideSlide)(
    ({theme: {spacing}}) => css`
        background-color: transparent;
        display: flex;
        align-items: center;
        flex-direction: column;
        cursor: grab;
        text-align: center;
        margin-bottom: ${spacing * 1.25}px;
        padding-bottom: ${spacing * 2.5}px;
    `
)

export const StyledNameBox = styled.div`
    ${({theme: {typography, spacing}}) => css`
        margin-bottom: ${spacing * 2}px;
        ${typography.textXl};
        font-weight: 700;
        padding-bottom: ${spacing}px;
        border-bottom: 1.714px solid rgba(127, 130, 157, 0.2);
    `}
`

export const StyledContentBox = styled.div`
    ${({theme: {spacing}}) => css`
        display: flex;
        flex-direction: column;
        gap: ${spacing}px;
        width: 100%;
        height: 100%;
    `}
`

export const StyledInfoContainer = styled.div`
    ${({theme: {typography}}) => css`
        display: flex;
        align-items: center;
        justify-content: space-around;
        ${typography.textXs};
    `}
`

export const StyledTooltipBox = styled.div`
    ${({theme: {spacing}}) => css`
        display: flex;
        align-self: center;
        gap: ${spacing * 1.5}px;
        & button {
            display: flex;
        }
    `}
`

export const StyledCircle = styled.div`
    ${({theme: {palette, mediaQueries}}) => css`
        border-radius: 50%;
        background-color: ${palette.primary[200]};
        width: 8px;
        height: 8px;
        ${mediaQueries.m} {
            width: 14px;
            height: 14px;
        }
    `}
`

export const StyledInfoBox = styled.div`
    ${({theme: {spacing, mediaQueries, typography}}) => css`
        ${typography.textXs}
        display: flex;
        align-items: center;
        gap: ${spacing / 2}px;
        ${mediaQueries.m} {
            ${typography.textLg}
        }
    `}
`

export const StyledInfoIcon = styled(InfoIcon)`
    ${({theme: {mediaQueries}}) => css`
        opacity: 0.7;
        width: 10px;
        height: 10px;
        cursor: pointer;
        ${mediaQueries.m} {
            width: 20px;
            height: 20px;
        }
    `}
`

export const StyledImageContainer = styled.div<{hasDefaultImage: boolean; hasCoupon?: boolean}>`
    ${({hasCoupon = false, hasDefaultImage, theme: {spacing}}) => css`
        width: 100%;
        padding-bottom: ${spacing * 2.5}px;
        position: relative;
        & img {
            width: 100%;
            border-radius: ${hasDefaultImage ? '12px' : '12px'};
            object-fit: ${hasDefaultImage ? 'contain' : 'cover'};
            border: ${hasDefaultImage ? '1px solid black' : 'none'};
            max-height: 200px;
            ${hasCoupon &&
            css`
                border: none;
            `}
        }
    `}
`

export const StyledButton = styled(Button)`
    ${({theme: {spacing, typography, mediaQueries}}) => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        ${typography.textMd}
        padding: ${spacing * 2}px ${spacing * 3}px;
        & > div {
            display: flex;
            flex-direction: column;
        }
        ${mediaQueries.m} {
            ${typography.text2Xl}
            padding: ${spacing * 2}px ${spacing * 4}px;
        }
    `}
`

export const StyledContainer = styled.div`
    ${({theme: {spacing}}) => css`
        display: flex;
        flex-direction: column;
        gap: ${spacing}px;
    `}
`

export const StyledButtonContent = styled.div<{discount?: boolean}>`
    ${({discount = false, theme: {mediaQueries, typography, spacing}}) => css`
        display: flex;
        align-items: center;
        gap: ${spacing / 2}px;
        ${typography.textXs} ${mediaQueries.m} {
            ${typography.text2Xl}
        }
        ${discount &&
        css`
            opacity: 0.6;
            text-decoration: line-through;
        `}
    `}
`

export const StyledPopoverContent = styled.div`
    ${({theme: {spacing}}) => css`
        max-width: 315px;
        padding: ${spacing}px;
        text-align: center;
        word-break: break-word;
    `}
`

export const StyledActivePlanIcon = styled.div`
    ${({theme: {mediaQueries}}) => css`
        position: absolute;
        top: 5%;
        right: 5%;
        background-color: white;
        cursor: pointer;
        width: 30px;
        height: 30px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        & svg {
            width: 17px;
            height: 20px;
            margin-top: -5px;
            margin-left: 4px;
        }
        ${mediaQueries.m} {
            width: 40px;
            height: 40px;
            & svg {
                width: 25px;
                height: 25px;
            }
        }
    `}
`

export const StyledActiveSubscrTooltipContent = styled.div`
    ${({theme: {spacing}}) => css`
        font-size: 16px;
        padding: ${spacing}px;
    `}
`

export const StyledCurrentPlan = styled.div`
    ${({theme: {spacing, palette}}) => css`
        display: flex;
        padding: ${spacing}px ${spacing}px ${spacing * 2}px ${spacing}px;
        flex-direction: column;
        align-items: center;
        align-self: stretch;

        border-radius: 8px;
        background: ${palette.neutral.white};
        box-shadow: 0px 2px 8px 0px rgba(2, 0, 49, 0.25);
    `}
`
export const StyledUpdatePlanButton = styled(Button)`
    ${({theme: {spacing, palette, typography}}) => css`
        ${typography.textMd}
        margin-top: ${spacing}px;
        display: flex;
        padding: ${spacing * 2}px ${spacing * 3}px;
        justify-content: center;
        align-items: center;

        border-radius: 100px;
        border: 2px solid ${palette.primary[200]};

        color: ${palette.primary[200]};
        text-align: center;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
    `}
`
export const StyledOpenGiftIcon = styled(OpenGiftIcon)`
    ${({theme: {spacing}}) => css`
        position: absolute;
        right: ${spacing * 2}px;
        top: ${spacing * 2}px;
        height: 40px;
        width: 40px;
        background-color: white;
        border-radius: 20px;
        padding: ${spacing / 2}px;
    `}
`
export const StyledPercentageOfDiscount = styled.div`
    ${({theme: {spacing, palette, typography}}) => css`
        position: absolute;
        left: ${spacing}px;
        top: ${spacing * 2}px;
        display: flex;
        padding: 4px 8px;
        justify-content: center;
        align-items: center;
        gap: 10px;
        border-radius: 6px;
        background: linear-gradient(0deg, #fc9f21 0%, #fc9f21 100%);

        color: ${palette.neutral.white};
        ${typography.textXxs}
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        text-transform: capitalize;
    `}
`

export const StyledDiscountText = styled.span`
    ${({theme: {typography, palette, mediaQueries}}) => css`
        color: ${palette.tertiary[50]};
        ${typography.textXs}
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        ${mediaQueries.m} {
            ${typography.text2Xl}
        }
    `}
`
